import { Link } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import signalLogo from "@/assets/signal-logo.svg";
import signalIcon from "@/assets/signal-icon.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { RoutePath } from "@/routes";
import { ACCOUNT_ROLE } from "@/constants";
import { accountApi } from "@/resources/account";
import { BrandSelector } from "./sidebar/BrandSelector";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { CollapsibleNavTooltip } from "./sidebar/CollapsibleNavTooltip";
import { NAV_ITEMS, ICON_CLASS, EXPANDED_ITEM, COLLAPSED_ITEM, ICON_CLASS_SM } from "./sidebar/constants";

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const { data: account } = accountApi.useGetAccount();
  const canManageWorkspace =
    account?.role === ACCOUNT_ROLE.OWNER ||
    account?.role === ACCOUNT_ROLE.ADMIN;
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || canManageWorkspace);

  return (
    <>
      <Sidebar
        collapsible="icon"
        className="border-r-0 [&_[data-sidebar=sidebar]]:!bg-[linear-gradient(to_bottom,#342799_0%,#4C32F2_33%,#A242A2_72%,#FF534C_100%)]"
      >
        <SidebarHeader className="p-4 pt-4 flex flex-col items-center justify-center">
          <Link to={RoutePath.Home} className="flex justify-center hover:opacity-80 transition-opacity">
            {collapsed ? (
              <img src={signalIcon} alt="Signal" className="h-6 w-6" />
            ) : (
              <img src={signalLogo} alt="Signal" className="h-10" />
            )}
          </Link>

          {!collapsed && <BrandSelector />}
        </SidebarHeader>

        <SidebarContent className="px-2 py-1">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <CollapsibleNavTooltip
                      collapsed={collapsed}
                      content={`${item.title}${item.disabled ? " (Coming soon)" : ""}`}
                    >
                      {item.disabled ? (
                        <span
                          className={`${collapsed ? COLLAPSED_ITEM : EXPANDED_ITEM} opacity-40 cursor-not-allowed`}
                          aria-disabled
                        >
                          <item.icon className={ICON_CLASS_SM} />
                          {!collapsed && (
                            <>
                              <span className="whitespace-nowrap">{item.title}</span>
                              <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-white/15 text-sidebar-foreground border-0">Soon</Badge>
                            </>
                          )}
                        </span>
                      ) : (
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={item.url}
                            end={item.url === RoutePath.Dashboard}
                            className={collapsed ? COLLAPSED_ITEM : EXPANDED_ITEM}
                            activeClassName="bg-black/15 text-sidebar-foreground font-normal"
                          >
                            <item.icon className={ICON_CLASS} />
                            {!collapsed && (
                              <>
                                <span className="whitespace-nowrap">{item.title}</span>
                                {item.adminOnly && (
                                  <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-white/20 text-sidebar-foreground border-0">Admin</Badge>
                                )}
                              </>
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      )}
                    </CollapsibleNavTooltip>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter collapsed={collapsed} />
      </Sidebar>
    </>
  );
}
